import * as reports from 'actions/reports';
import { sub, startOfMonth, endOfMonth } from 'date-fns';
import { formatIsoDate, formatUtcIsoDateTime } from 'helpers/DateHelper';
import { convertToNumber } from 'helpers/StringHelper';
import strings from 'localization/strings';

const getFromMonthInputField = monthsBack => ({
    name: 'from',
    label: 'From month',
    type: 'month',
    defaultValueGetter: () => startOfMonth(sub(new Date(), { months: monthsBack }))
});

const getToMonthInputField = monthsBack => ({
    name: 'to',
    label: 'To month',
    type: 'month',
    defaultValueGetter: () => endOfMonth(sub(new Date(), { months: monthsBack }))
});

const timePeriodGroupingInputField = {
    name: 'timePeriodGrouping',
    label: 'Group by',
    type: 'dropdown',
    data: [{ label: 'day', value: 'day' }, { label: 'month', value: 'month' }, { label: 'year', value: 'year' }],
    defaultValueGetter: () => 'month'
};

const dateInputField = {
    name: 'date',
    label: 'Date',
    type: 'date',
    defaultValueGetter: () => new Date()
};

const storageSiteCapacityUtilizationThresholdInputField = {
    name: 'storageSiteCapacityUtilizationThreshold',
    label: 'Capacity utilization threshold (%)',
    type: 'number',
    defaultValueGetter: () => 30
};

const parseCategory = value => value === 'all'
    ? undefined
    : value;

const adminReportDefinitions = {};

strings.addLoadCallback(appContext => {
    const categoryDropdownInputField = {
        name: 'category',
        label: 'Category',
        type: 'dropdown',
        data: [{ title: 'all', key: 'all' }].concat(appContext.storageGroupCategories).map(o => ({ label: o.title, value: o.key })),
        defaultValueGetter: () => 'all'
    };

    adminReportDefinitions.earnings = {
        title: 'Earnings',
        inputFields: [
            getFromMonthInputField(12),
            getToMonthInputField(1),
            categoryDropdownInputField,
            timePeriodGroupingInputField
        ],
        fetchData: values => {
            const inputFields = values.inputFields[adminReportDefinitions.earnings.key];
            const parameters = {
                from: formatIsoDate(startOfMonth(inputFields.from, appContext)),
                to: formatIsoDate(endOfMonth(inputFields.to, appContext)),
                category: parseCategory(inputFields.category),
                timePeriodGrouping: inputFields.timePeriodGrouping
            };
            return values.format === 'excel'
                ? reports.getEarningsReportExcelFile(parameters)
                : reports.getEarningsReport(parameters);
        }
    };

    adminReportDefinitions.newActors = {
        title: 'New actors',
        inputFields: [
            { name: 'since', label: 'Since', type: 'date', defaultValueGetter: () => sub(new Date(), { months: 1 }) }
        ],
        fetchData: values => {
            const inputFields = values.inputFields[adminReportDefinitions.newActors.key];
            const parameters = { since: formatIsoDate(inputFields.since, appContext) };
            return values.format === 'excel'
                ? reports.getNewActorsReportExcelFile(parameters)
                : reports.getNewActorsReport(parameters);
        }
    };

    adminReportDefinitions.kpis = {
        title: 'KPIs',
        inputFields: [
            getFromMonthInputField(12),
            getToMonthInputField(1),
            categoryDropdownInputField,
            timePeriodGroupingInputField
        ],
        fetchData: values => {
            const inputFields = values.inputFields[adminReportDefinitions.kpis.key];
            const parameters = {
                from: formatIsoDate(startOfMonth(inputFields.from), appContext),
                to: formatIsoDate(endOfMonth(inputFields.to), appContext),
                category: parseCategory(inputFields.category),
                timePeriodGrouping: inputFields.timePeriodGrouping
            };
            return values.format === 'excel'
                ? reports.getKpisReportExcelFile(parameters)
                : reports.getKpisReport(parameters);
        }
    };

    adminReportDefinitions.storage365Payout = {
        title: 'Storage365 payout',
        inputFields: [
            getFromMonthInputField(1),
            getToMonthInputField(1)
        ],
        fetchData: values => {
            const inputFields = values.inputFields[adminReportDefinitions.storage365Payout.key];
            const parameters = {
                from: formatUtcIsoDateTime(startOfMonth(inputFields.from)),
                to: formatUtcIsoDateTime(endOfMonth(inputFields.to))
            };
            return values.format === 'excel'
                ? reports.getStorage365PayoutReportExcelFile(parameters)
                : reports.getStorage365PayoutReport(parameters);
        }
    };

    adminReportDefinitions.paymentProviderTransactionSummary = {
        title: 'Payment provider transaction summary',
        inputFields: [
            getFromMonthInputField(1),
            getToMonthInputField(1)
        ],
        fetchData: values => {
            const inputFields = values.inputFields[adminReportDefinitions.paymentProviderTransactionSummary.key];
            const parameters = {
                from: formatUtcIsoDateTime(startOfMonth(inputFields.from)),
                to: formatUtcIsoDateTime(endOfMonth(inputFields.to))
            };
            return values.format === 'excel'
                ? reports.getPaymentProviderTransactionSummaryExcelFile(parameters)
                : reports.getPaymentProviderTransactionSummary(parameters);
        }
    };

    adminReportDefinitions.storageStatistics = {
        title: 'Storage statistics',
        inputFields: [
            dateInputField,
            categoryDropdownInputField,
            storageSiteCapacityUtilizationThresholdInputField
        ],
        fetchData: values => {
            const inputFields = values.inputFields[adminReportDefinitions.storageStatistics.key];
            const storageSiteCapacityUtilizationThreshold = convertToNumber(inputFields.storageSiteCapacityUtilizationThreshold);
            const parameters = {
                date: formatUtcIsoDateTime(inputFields.date),
                category: parseCategory(inputFields.category),
                storageSiteCapacityUtilizationThreshold: storageSiteCapacityUtilizationThreshold === undefined
                    ? undefined
                    : storageSiteCapacityUtilizationThreshold / 100
            };
            return values.format === 'excel'
                ? reports.getStorageStatisticsExcelFile(parameters)
                : reports.getStorageStatistics(parameters);
        }
    };

    adminReportDefinitions.capacityUtilizationPerStorageSite = {
        title: 'Capacity utilization per storage site',
        inputFields: [
            dateInputField,
            categoryDropdownInputField,
            storageSiteCapacityUtilizationThresholdInputField
        ],
        fetchData: values => {
            const inputFields = values.inputFields[adminReportDefinitions.capacityUtilizationPerStorageSite.key];
            const storageSiteCapacityUtilizationThreshold = convertToNumber(inputFields.storageSiteCapacityUtilizationThreshold);
            const parameters = {
                date: formatUtcIsoDateTime(inputFields.date),
                category: parseCategory(inputFields.category),
                storageSiteCapacityUtilizationThreshold: storageSiteCapacityUtilizationThreshold === undefined
                    ? undefined
                    : storageSiteCapacityUtilizationThreshold / 100
            };
            return values.format === 'excel'
                ? reports.getCapacityUtilizationPerStorageSiteExcelFile(parameters)
                : reports.getCapacityUtilizationPerStorageSite(parameters);
        }
    };

    adminReportDefinitions.storageSiteCapacityUtilizationTimeline = {
        title: 'Storage site capacity utilization timeline',
        inputFields: [
            { name: 'storageSiteIds', label: 'Storage site IDs', helperText: 'Comma-separated list of storage site IDs.', defaultValueGetter: () => '' }
        ],
        fetchData: values => {
            const inputFields = values.inputFields[adminReportDefinitions.storageSiteCapacityUtilizationTimelinekey];
            const parameters = {
                storageSiteIds: inputFields.storageSiteIds
            };
            return values.format === 'excel'
                ? reports.getStorageSiteCapacityUtilizationTimelineExcelFile(parameters)
                : reports.getStorageSiteCapacityUtilizationTimeline(parameters);
        }
    };

    Object.keys(adminReportDefinitions).forEach(key => {
        adminReportDefinitions[key].key = key;
    });
});

export default adminReportDefinitions;
